/**
 * Widget Dashboard "Météo 7 jours" — orchestrateur.
 *
 * Architecture visuelle :
 *   1. Header replié (icône + T° + commune + chips actuels + chevron)
 *   2. Contenu déployé (si non replié) :
 *      - Strip 7 jours (icône + min/max)
 *      - Panneau "heure explorée" (synchronisé avec le curseur du graphique)
 *      - Graphique SVG aire dégradée (T° / Pluie / Vent / Hum.)
 *      - Footer : légende + onglets de bascule de variable
 *
 * Conçu pour usage terrain (mains froides/mouillées, 4G dégradée) :
 *   - Tap + drag sur la courbe (pointer events) pour explorer une heure précise
 *   - Échelle Y dynamique par variable (auto-scale T°/humidité, bornes fixes pluie/vent)
 *   - staleTime 30 min, refetchOnFocus off (économie 4G)
 *
 * Découpage en sous-composants sous `./meteo/` — voir `meteo/MeteoContenu.tsx`
 * pour l'assemblage interne. Source données : `trpc.dashboard.meteo7j` →
 * Open-Meteo via BAN (geocoding INSEE → lat/lon).
 *
 * Limitations connues :
 *   - Pas encore de sous-titre "parcelle" (BBCH = futur, pas en BDD).
 *   - Pas de cache localStorage offline (TODO V1.5, pattern persister).
 */

import { Card } from "@champ-erp/ui";
import { useState } from "react";

import { MeteoContenu } from "./meteo/MeteoContenu.js";
import { MeteoEtatVide } from "./meteo/MeteoError.js";
import { MeteoSkeleton } from "./meteo/MeteoSkeleton.js";
import { useMeteo7j } from "./meteo/use-meteo-7j.js";

export function WidgetMeteoTimeline() {
  const { data, isLoading, isError } = useMeteo7j();

  // État replié — TOUJOURS replié à l'arrivée sur le dashboard. Pas de
  // persistance en localStorage : le widget ne doit pas mémoriser l'état
  // déployé d'une session à l'autre. Si le vigneron veut consulter les détails,
  // il déploie ; la prochaine fois, il repart d'une vue compacte.
  const [replie, setReplie] = useState<boolean>(true);

  // Décision design : le header live (barre compacte avec T° / commune / chips)
  // reste IDENTIQUE en replié et en déployé. Déployer = juste ajouter le
  // contenu en dessous, sans rien changer du header. Padding du Card constant
  // (`p-3`) → pas de transition visuelle dérangeante.
  return (
    <Card className="p-3 min-[1100px]:col-span-2">
      {isLoading || (!data && !isError) ? (
        <MeteoSkeleton />
      ) : isError || !data ? (
        <MeteoEtatVide message="Service météo momentanément indisponible." />
      ) : data.status === "commune_inconnue" ? (
        <MeteoEtatVide message="Renseignez la commune de votre siège dans les paramètres pour voir la météo locale." />
      ) : data.status === "geocode_indisponible" ? (
        <MeteoEtatVide message="Impossible de localiser votre commune. Vérifiez le code INSEE saisi." />
      ) : data.status === "meteo_indisponible" ? (
        <MeteoEtatVide message="Service météo momentanément indisponible. Réessayez dans quelques minutes." />
      ) : (
        <MeteoContenu
          hourly={data.hourly}
          fetchedAt={data.fetched_at}
          commune={data.commune}
          replie={replie}
          onToggleReplie={() => setReplie((r) => !r)}
        />
      )}
    </Card>
  );
}
